<template>
    <div class="checkin-detail-page" v-loading="loading">
        <div class="container" v-if="checkIn">
            <!-- Back Navigation -->
            <div class="back-nav" @click="goBack">
                <el-icon>
                    <ArrowLeft />
                </el-icon> 返回课程
            </div>

            <div class="detail-wrapper">
                <!-- Left: Content & Images -->
                <div class="content-side">
                    <div class="glass-card main-card">
                        <div class="user-header">
                            <el-avatar :size="56" :src="checkIn.user?.avatar" class="avatar">
                                {{ checkIn.user?.username?.charAt(0) }}
                            </el-avatar>
                            <div class="user-info">
                                <h2 class="username">{{ checkIn.user?.username }}</h2>
                                <p class="time">{{ formatDate(checkIn.createdAt) }}</p>
                            </div>
                            <div class="context-tag" v-if="checkIn.node">
                                <span class="label">正在学习：</span>
                                <el-tag size="small" effect="plain" @click="goToActivityNode">
                                    {{ checkIn.node?.title }}
                                </el-tag>
                            </div>
                        </div>

                        <div class="checkin-text">
                            {{ checkIn.content }}
                        </div>

                        <div v-if="checkIn.imageUrls?.length > 0" class="image-gallery">
                            <el-image v-for="(img, idx) in checkIn.imageUrls" :key="idx" :src="img"
                                :preview-src-list="checkIn.imageUrls" :initial-index="idx" fit="cover"
                                class="gallery-img" />
                        </div>

                        <div class="action-bar">
                            <div class="social-stats">
                                <div class="stat-item" :class="{ active: checkIn.liked }" @click="handleLike">
                                    <el-icon>
                                        <Pointer />
                                    </el-icon>
                                    <span>{{ checkIn.likeCount || 0 }}</span>
                                </div>
                                <div class="stat-item" :class="{ active: checkIn.favorited }" @click="handleFavorite">
                                    <el-icon>
                                        <Star />
                                    </el-icon>
                                    <span>{{ checkIn.favoriteCount || 0 }}</span>
                                </div>
                                <div class="stat-item">
                                    <el-icon>
                                        <ChatDotRound />
                                    </el-icon>
                                    <span>{{ checkIn.commentCount || 0 }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Comment Section -->
                    <div class="glass-card comment-card">
                        <h3>评论区 ({{ checkIn.commentCount || 0 }})</h3>

                        <div class="comment-input-wrapper">
                            <el-input v-model="newComment" type="textarea" :rows="3" placeholder="输入你的鼓励和想法..."
                                resize="none" />
                            <div class="input-footer">
                                <el-button type="primary" round @click="submitComment" :loading="submitting">
                                    发表评论
                                </el-button>
                            </div>
                        </div>

                        <div class="comment-list">
                            <div v-if="comments.length === 0" class="empty-comments">
                                <el-empty description="暂时还没有评论，快来抢沙发吧~" :image-size="80" />
                            </div>
                            <comment-item v-for="comment in commentTree" :key="comment.id" :comment="comment"
                                :replies="comment.replies" type="checkin" :targetId="checkInId"
                                @reply-submitted="fetchData" />
                        </div>
                    </div>
                </div>

                <!-- Right: Activity Sidebar -->
                <div class="info-side">
                    <div class="glass-card activity-info" v-if="checkIn.activity">
                        <h4>所属活动</h4>
                        <div class="activity-card" @click="goToActivity">
                            <el-image :src="checkIn.activity.coverImage || defaultCover" fit="cover"
                                class="activity-cover" />
                            <div class="activity-title">{{ checkIn.activity.title }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, Pointer, ChatDotRound, Star } from '@element-plus/icons-vue'
import api from '@/utils/api'
import dayjs from 'dayjs'
import { computed } from 'vue'
import CommentItem from '@/components/CommentItem.vue'

const route = useRoute()
const useRouterObj = useRouter()
const checkInId = route.params.id

const loading = ref(true)
const checkIn = ref(null)
const comments = ref([])
const newComment = ref('')
const submitting = ref(false)

const commentTree = computed(() => {
    const items = JSON.parse(JSON.stringify(comments.value.map(c => ({
        ...c,
        author: c.user, // Adapt to CommentItem expectation
        authorId: c.userId
    }))))
    const map = {}
    const tree = []

    items.forEach(item => {
        item.replies = []
        map[item.id] = item
    })

    items.forEach(item => {
        if (item.parentId && map[item.parentId]) {
            map[item.parentId].replies.push(item)
        } else {
            tree.push(item)
        }
    })

    return tree
})

const defaultCover = "https://via.placeholder.com/400x225?text=Math+Course"

const fetchData = async () => {
    loading.value = true
    try {
        const res = await api.get(`/api/checkins/${checkInId}`)
        checkIn.value = res.data.checkIn

        const commentRes = await api.get(`/api/checkins/${checkInId}/comments`)
        comments.value = commentRes.data.comments || []
    } catch (e) {
        ElMessage.error('获取打卡详情失败')
        useRouterObj.back()
    } finally {
        loading.value = false
    }
}

const formatDate = (ts) => {
    if (!ts) return ''
    return dayjs(ts * 1000).format('YYYY-MM-DD HH:mm')
}

const goBack = () => {
    useRouterObj.back()
}

const goToActivity = () => {
    useRouterObj.push(`/activities/${checkIn.value.activityId}`)
}

const goToActivityNode = () => {
    useRouterObj.push(`/activities/${checkIn.value.activityId}/learn?nodeId=${checkIn.value.nodeId}`)
}

const handleLike = async () => {
    try {
        const res = await api.post(`/api/checkins/${checkInId}/like`)
        checkIn.value.liked = res.data.liked
        checkIn.value.likeCount = res.data.liked ? (checkIn.value.likeCount + 1) : (checkIn.value.likeCount - 1)
        ElMessage.success(res.data.liked ? '已点赞' : '已取消点赞')
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const handleFavorite = async () => {
    try {
        const res = await api.post(`/api/checkins/${checkInId}/favorite`)
        checkIn.value.favorited = res.data.favorited
        checkIn.value.favoriteCount = res.data.favorited ? (checkIn.value.favoriteCount + 1) : (checkIn.value.favoriteCount - 1)
        ElMessage.success(res.data.favorited ? '已收藏' : '已取消收藏')
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const submitComment = async () => {
    if (!newComment.value.trim()) return
    submitting.value = true
    try {
        const res = await api.post(`/api/checkins/${checkInId}/comments`, {
            content: newComment.value
        })
        comments.value.push(res.data.comment)
        checkIn.value.commentCount++
        newComment.value = ''
        ElMessage.success('评论成功')
    } catch (e) {
        ElMessage.error('评论失败')
    } finally {
        submitting.value = false
    }
}

onMounted(() => {
    fetchData()
})
</script>

<style scoped>
.checkin-detail-page {
    background: #f8fafc;
    min-height: 100vh;
    padding: 30px 20px 80px;
}

.container {
    max-width: 1100px;
    margin: 0 auto;
}

.back-nav {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    color: #64748b;
    cursor: pointer;
    margin-bottom: 24px;
    transition: color 0.2s;
}

.back-nav:hover {
    color: #3b82f6;
}

.detail-wrapper {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 30px;
}

@media (max-width: 900px) {
    .detail-wrapper {
        grid-template-columns: 1fr;
    }
}

.glass-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    padding: 30px;
    margin-bottom: 30px;
}

.main-card {
    border-top: 4px solid #3b82f6;
}

.user-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    position: relative;
}

.user-info .username {
    font-size: 20px;
    margin: 0;
    color: #1e293b;
}

.user-info .time {
    font-size: 14px;
    color: #94a3b8;
    margin: 4px 0 0;
}

.context-tag {
    margin-left: auto;
    font-size: 13px;
    color: #64748b;
}

.context-tag .el-tag {
    cursor: pointer;
}

.checkin-text {
    font-size: 17px;
    line-height: 1.6;
    color: #334155;
    margin-bottom: 24px;
    white-space: pre-wrap;
}

.image-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 30px;
}

.gallery-img {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 12px;
    cursor: zoom-in;
    transition: transform 0.2s;
}

.gallery-img:hover {
    transform: scale(1.02);
}

.action-bar {
    padding-top: 20px;
    border-top: 1px solid #f1f5f9;
}

.social-stats {
    display: flex;
    gap: 30px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #64748b;
    cursor: pointer;
    font-size: 15px;
    transition: all 0.2s;
}

.stat-item:hover {
    color: #3b82f6;
}

.stat-item.active {
    font-weight: bold;
}

.stat-item.active.stat-item {
    color: #3b82f6;
}

.stat-item.active .el-icon {
    font-size: 1.1em;
}

.comment-card h3 {
    margin-top: 0;
    margin-bottom: 20px;
}

.comment-input-wrapper {
    margin-bottom: 40px;
}

.input-footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}

/* Stale comment styles removed - now handled by CommentItem component */

.activity-info h4 {
    margin-top: 0;
    margin-bottom: 16px;
    color: #64748b;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.activity-card {
    cursor: pointer;
    border-radius: 12px;
    overflow: hidden;
    background: #f8fafc;
    transition: transform 0.2s;
}

.activity-card:hover {
    transform: translateY(-2px);
}

.activity-cover {
    width: 100%;
    aspect-ratio: 16/9;
}

.activity-title {
    padding: 12px;
    font-weight: 600;
    font-size: 14px;
    color: #1e293b;
}

.empty-comments {
    padding: 40px 0;
}
</style>
